import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Download } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PurchaseOrders() {
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    supplier_id: '', items: [{ description: '', quantity: 1, price: 0 }],
    notes: '', terms: '', vat_rate: 15, delivery_date: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [ordersRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/purchase-orders`, { withCredentials: true }),
        axios.get(`${API}/suppliers`, { withCredentials: true })
      ]);
      setOrders(ordersRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/purchase-orders`, formData, { withCredentials: true });
      toast.success('Purchase order created');
      setShowDialog(false);
      setFormData({ supplier_id: '', items: [{ description: '', quantity: 1, price: 0 }], notes: '', terms: '', vat_rate: 15, delivery_date: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to create purchase order');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/purchase-orders/${id}`, { status }, { withCredentials: true });
      toast.success('Status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const downloadPDF = async (po) => {
    try {
      const { data } = await axios.get(`${API}/purchase-orders/${po.id}/pdf`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${po.po_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const addItem = () => setFormData({ ...formData, items: [...formData.items, { description: '', quantity: 1, price: 0 }] });

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    setFormData({ ...formData, items: newItems });
  };

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-8" data-testid="purchase-orders-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}} data-testid="po-title">Purchase Orders</h1>
          <p className="mt-2 text-zinc-600">Manage purchase orders to suppliers</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="bg-black text-white hover:bg-zinc-800 gap-2" data-testid="create-po-button">
              <Plus size={18} /> Create PO
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Create Purchase Order</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Supplier</Label>
                <Select value={formData.supplier_id} onValueChange={(value) => setFormData({...formData, supplier_id: value})}>
                  <SelectTrigger className="mt-2" data-testid="po-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Line Items</Label>
                <div className="mt-2 space-y-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2">
                      <Input placeholder="Description" value={item.description} onChange={(e) => updateItem(index, 'description', e.target.value)} className="col-span-6" data-testid={`po-item-desc-${index}`} />
                      <Input type="number" placeholder="Qty" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', e.target.value)} className="col-span-3" data-testid={`po-item-qty-${index}`} />
                      <Input type="number" placeholder="Price" value={item.price} onChange={(e) => updateItem(index, 'price', e.target.value)} className="col-span-3" data-testid={`po-item-price-${index}`} />
                    </div>
                  ))}
                </div>
                <Button type="button" onClick={addItem} variant="outline" className="mt-2" data-testid="po-add-item-button">Add Item</Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">VAT Rate (%)</Label>
                  <Input type="number" value={formData.vat_rate} onChange={(e) => setFormData({...formData, vat_rate: parseFloat(e.target.value) || 0})} data-testid="po-vat-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Delivery Date</Label>
                  <Input type="date" value={formData.delivery_date} onChange={(e) => setFormData({...formData, delivery_date: e.target.value})} data-testid="po-delivery-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Notes</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={2} data-testid="po-notes-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Terms</Label>
                <Textarea value={formData.terms} onChange={(e) => setFormData({...formData, terms: e.target.value})} rows={2} data-testid="po-terms-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
              </div>
              <Button type="submit" className="w-full bg-black text-white hover:bg-zinc-800" data-testid="submit-po-button">Create PO</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="po-table">
            <thead className="border-b border-zinc-200 bg-zinc-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">PO Number</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Supplier</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Total</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Status</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Created</th>
                <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-zinc-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {orders.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-12 text-center text-zinc-500" data-testid="no-pos">No purchase orders yet.</td></tr>
              ) : orders.map((po) => (
                <tr key={po.id} className="hover:bg-zinc-50 transition-colors" data-testid={`po-row-${po.id}`}>
                  <td className="px-6 py-4 font-medium text-zinc-900">{po.po_number}</td>
                  <td className="px-6 py-4 text-zinc-600 text-sm">{po.supplier_name}</td>
                  <td className="px-6 py-4 font-semibold text-zinc-900">R {po.total.toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <Select value={po.status} onValueChange={(value) => updateStatus(po.id, value)}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="sent">Sent</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-6 py-4 text-zinc-600 text-sm">{new Date(po.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => downloadPDF(po)} className="text-blue-600 hover:text-blue-800" data-testid={`download-po-${po.id}`}><Download size={18} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
